import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import {
  EmailField,
  EntityProfilePage,
  ManufacturerField,
  StatusIndicator,
  TextInput,
} from '../../components/form';
import { useDebounce } from '../../hooks/debounce';
import { useAppSelector } from '../../store/hooks';
import { Technician } from '../../types';
import { logError, optionalEmailSchema, preventPaste, showToast } from '../../utils';
import { useManufacturerData } from '../manufacturer/use-manufacturer-data';
import { selectManageManufacturers, selectManageTechnicians } from '../user';
import {
  useAddTechnicianMutation,
  useGetTechnicianByIdQuery,
  useUpdateTechnicianMutation,
  useValidateInitialsUniquenessQuery,
} from './technicians-api-slice';

// Define Zod schema for form validation
const technicianFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(250, 'First name is too long'),
  lastName: z.string().min(1, 'Last name is required').max(250, 'Last name is too long'),
  initials: z
    .string()
    .min(2, 'Initials must be at least 2 characters')
    .max(3, 'Initials cannot exceed 3 characters'),
  email: optionalEmailSchema,
  active: z.boolean(),
  manufacturerId: z.number().min(1, 'Manufacturer is required'),
});

// Extract type from Zod schema
type TechnicianFormValues = z.infer<typeof technicianFormSchema>;

const TechnicianProfilePage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  // Get current user from Redux store
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageTechnicians = useAppSelector(selectManageTechnicians);

  const currentUser = useAppSelector(state => state.user.currentUser);
  const currentUserManufacturerId = currentUser?.manufacturerId;

  // Get technician data
  const {
    data: technician,
    isLoading: isTechnicianLoading,
    error,
    refetch: refetchTechnician,
  } = useGetTechnicianByIdQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  // Use the shared manufacturer data hook
  const { manufacturers, isLoading: isLoadingManufacturers } = useManufacturerData({
    isEditMode,
    profileManufacturerId: technician?.manufacturerId,
    currentUserManufacturerId,
  });

  const [updateTechnician, { isLoading: isUpdating }] = useUpdateTechnicianMutation();
  const [addTechnician, { isLoading: isAdding }] = useAddTechnicianMutation();

  const isLoading = isTechnicianLoading || isLoadingManufacturers;
  const isInProcess = isUpdating || isAdding;

  // Set up React Hook Form with Zod resolver
  const form = useForm<TechnicianFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      initials: '',
      email: '',
      active: true,
      manufacturerId: (!canManageManufacturers && currentUserManufacturerId) || 0,
    },
    resolver: zodResolver(technicianFormSchema),
    mode: 'onChange',
  });

  const {
    control,
    reset,
    setValue,
    setError,
    trigger,
    formState: { isDirty, isValid },
  } = form;

  // Watch initials and manufacturerId for validation
  const watchedInitials = useWatch({ control, name: 'initials' });
  const watchedManufacturerId = useWatch({ control, name: 'manufacturerId' });

  // Debounce initials to avoid too many API calls
  const debouncedInitials = useDebounce(watchedInitials || '', 500);

  // Query to validate initials uniqueness
  const { data: initialsValidationData } = useValidateInitialsUniquenessQuery(
    {
      technicianId: id ? parseInt(id, 10) : null,
      initials: debouncedInitials || '',
      manufacturerId: watchedManufacturerId || 0,
    },
    {
      skip:
        !debouncedInitials ||
        !watchedManufacturerId ||
        watchedManufacturerId === 0 ||
        debouncedInitials.length < 2,
    }
  );

  // Check for initials duplicate when validation data is available
  useEffect(() => {
    // Skip validation if initials are too short or manufacturer is not selected
    if (
      !debouncedInitials ||
      !watchedManufacturerId ||
      watchedManufacturerId === 0 ||
      debouncedInitials.length < 2
    ) {
      // Re-run schema validation instead of blanket-clearing errors so the Zod
      // length/required errors stay visible while only the uniqueness error is dropped.
      if (!debouncedInitials || debouncedInitials.length < 2) {
        void trigger('initials');
      }
      return;
    }

    // Process validation data whenever it's available
    if (initialsValidationData !== undefined) {
      if (initialsValidationData.isUnique === false) {
        const errorMessage = 'These initials already exist for this manufacturer.';
        // Set error on the initials field
        setError('initials', { type: 'manual', message: errorMessage }, { shouldFocus: false });
      } else if (initialsValidationData.isUnique === true) {
        // Unique: re-run schema validation rather than clearing every error, otherwise a
        // legacy schema error (e.g. initials longer than 3 chars) would be wiped out and the
        // Save button would stay disabled with no visible reason.
        void trigger('initials');
      }
    }
    // Note: We don't clear errors when data is undefined, as this could be a loading state
    // The error will persist until we get confirmation the initials are unique
  }, [initialsValidationData, debouncedInitials, watchedManufacturerId, setError, trigger]);

  // Handle form submission with React Hook Form
  const onSubmit = async (data: TechnicianFormValues) => {
    try {
      // Normalize email: convert empty string or undefined to null for optional field
      const email = data.email && data.email.trim() !== '' ? data.email.trim() : null;

      if (id) {
        await updateTechnician({
          id,
          firstName: data.firstName,
          lastName: data.lastName,
          initials: data.initials,
          email: email,
          active: data.active,
          manufacturerId: data.manufacturerId,
        }).unwrap();

        showToast('Technician successfully updated', 'success');
      } else {
        const newTechnician: Omit<Technician, 'id'> = {
          firstName: data.firstName,
          lastName: data.lastName,
          initials: data.initials,
          email: email,
          active: data.active,
          manufacturerId: data.manufacturerId,
        };

        await addTechnician({
          technicianData: newTechnician,
        }).unwrap();

        showToast('Technician successfully created', 'success');
      }

      return Promise.resolve();
    } catch (error) {
      logError('Error updating technician:', error);
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  };

  useEffect(() => {
    if (id) {
      refetchTechnician();
    }
  }, [id, refetchTechnician]);

  // For new technician creation, set manufacturer ID to current user's manufacturer for non-admin users
  useEffect(() => {
    if (!isEditMode && !canManageManufacturers && currentUserManufacturerId) {
      // Set the manufacturer ID to the current user's manufacturer for non-admin users
      setValue('manufacturerId', currentUserManufacturerId);
    }
  }, [isEditMode, canManageManufacturers, currentUserManufacturerId, setValue]);

  useEffect(() => {
    if (technician) {
      const effectiveManufacturerId =
        !canManageManufacturers && currentUserManufacturerId
          ? currentUserManufacturerId
          : technician?.manufacturerId;

      reset({
        firstName: technician.firstName,
        lastName: technician.lastName,
        initials: technician.initials,
        email: technician.email || '',
        active: technician.active,
        manufacturerId: effectiveManufacturerId ?? undefined,
      });

      // Surface any validation errors on the loaded data immediately (e.g. legacy
      // technicians whose initials are longer than the allowed 2-3 characters).
      // Without this, such records leave the Save button silently disabled with no
      // visible reason, so the user can't tell why their edits (or a status toggle)
      // won't save.
      if (canManageTechnicians) {
        void trigger();
      }
    }
  }, [
    technician,
    reset,
    trigger,
    canManageManufacturers,
    currentUserManufacturerId,
    canManageTechnicians,
  ]);

  // Find the manufacturer name from the technician's manufacturerId or manufacturer field
  const getManufacturerName = () => {
    if (technician) {
      if (technician.manufacturerId) {
        return (
          manufacturers.find(m => m.id === technician.manufacturerId)?.licenseeName ?? 'Unknown'
        );
      } else if (technician.manufacturer) {
        return technician.manufacturer;
      }
    }

    if (currentUserManufacturerId && !canManageManufacturers) {
      return manufacturers.find(m => m.id === currentUserManufacturerId)?.licenseeName ?? 'Unknown';
    }

    return 'None';
  };

  return (
    <EntityProfilePage
      title="Technician Profile"
      entity={technician}
      isLoading={isLoading}
      error={error}
      isSaving={isInProcess}
      form={form}
      hasChanges={isDirty}
      isValid={isValid}
      isReadOnly={!canManageTechnicians}
      onSubmit={onSubmit}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Manufacturer field */}
            {canManageManufacturers && (
              <ManufacturerField
                control={control}
                name="manufacturerId"
                manufacturers={manufacturers}
                isReadOnly={!canManageManufacturers}
                getManufacturerName={getManufacturerName}
                isLoading={isLoadingManufacturers}
              />
            )}

            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <TextInput
                name="firstName"
                control={control}
                label="First Name"
                isReadOnly={!canManageTechnicians}
                required={true}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <TextInput
                name="lastName"
                control={control}
                label="Last Name"
                isReadOnly={!canManageTechnicians}
                required={true}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <EmailField
                emailName="email"
                control={control}
                handlePaste={preventPaste}
                isReadOnly={!canManageTechnicians}
                required={false}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={4}>
                {/* Initials */}
                <Grid item xs={12} sm={3}>
                  <TextInput
                    name="initials"
                    control={control}
                    label="Initials"
                    isReadOnly={!canManageTechnicians}
                    required={true}
                    maxLength={3}
                  />
                </Grid>

                {/* Status toggle */}
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <StatusIndicator
                    name="active"
                    control={control}
                    isReadOnly={!canManageTechnicians}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EntityProfilePage>
  );
};

export default TechnicianProfilePage;
